<div class="creditorwatch-report-dialog">
  <mat-dialog-content class="max-height-100-vh">

    <div class="report-header">
      <div class="report-title">
        <span class="report-kicker">CreditorWatch Report</span>
        <h3 class="report-organisation">{{report.summary.organisationName ?? 'Unknown entity'}}</h3>
      </div>
      <div class="report-scores">
        @if (report.summary.currentCreditScore !== null) {
          <div class="score-tile">
            <span class="score-value">{{report.summary.currentCreditScore}}</span>
            <span class="score-label">Score</span>
          </div>
        }
        @if (report.summary.currentCreditRating) {
          <div class="score-tile">
            <span class="score-value">{{report.summary.currentCreditRating}}</span>
            <span class="score-label">Rating</span>
          </div>
        }
      </div>
    </div>

    <div class="report-identifiers">
      @if (report.summary.australianBusinessNumber) {
        <span class="identifier"><span class="identifier-label">ABN</span> {{report.summary.australianBusinessNumber}}</span>
      }
      @if (report.summary.organisationNumber) {
        <span class="identifier"><span class="identifier-label">ACN</span> {{report.summary.organisationNumber}}</span>
      }
      @if (report.riskTotals.relatedEntityCount) {
        <span class="identifier">{{report.riskTotals.relatedEntityCount}} related entities</span>
      }
    </div>

    @if (report.riskTotals.clean) {
      <div class="risk-banner risk-banner-clear">No adverse items on this entity or any related entity</div>
    } @else {
      <div class="risk-banner">
        @if (report.riskTotals.entityAdverseItemCount) {
          <span class="risk-chip risk-chip-alert">{{report.riskTotals.entityAdverseItemCount}} on this entity</span>
        }
        @if (report.riskTotals.relatedAdverseItemCount) {
          <span class="risk-chip">{{report.riskTotals.relatedAdverseItemCount}} on related entities</span>
        }
        @if (report.riskTotals.highRiskEntityCount) {
          <span class="risk-chip">{{report.riskTotals.highRiskEntityCount}} flagged high risk</span>
        }
      </div>
    }

    <h4 class="section-heading">Entity Risk Data</h4>
    @if (report.entityAdverseGroups.length) {
      @for (group of report.entityAdverseGroups; track group.title) {
        <div class="adverse-group">
          <div class="adverse-group-title">{{group.title}} ({{group.items.length}})</div>
          @for (item of group.items; track $index) {
            <div class="adverse-item">
              <div class="adverse-item-headline" (click)="toggleFields(item)">
                <span class="mdi" [class.mdi-chevron-right]="!isShowingFields(item)" [class.mdi-chevron-down]="isShowingFields(item)"></span>
                <span>{{item.headline}}</span>
              </div>
              @if (isShowingFields(item)) {
                <div class="adverse-fields">
                  @for (field of item.fields; track field.label) {
                    <div class="adverse-field">
                      <span class="adverse-field-label">{{field.label}}</span>
                      <span class="adverse-field-value">{{field.value}}</span>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    } @else {
      <div class="no-data">No Data</div>
    }

    <h4 class="section-heading">Related Risk Data</h4>
    @if (report.relatedIndividuals.length) {
      @for (individual of report.relatedIndividuals; track $index) {
        <div class="individual">
          <div class="individual-header">
            <span class="individual-name">{{individual.fullName}}</span>
            @if (individual.adverseItemCount) {
              <span class="risk-chip risk-chip-alert">{{individual.adverseItemCount}} adverse</span>
            }
          </div>
          @if (personSummary(individual)) {
            <div class="individual-detail">{{personSummary(individual)}}</div>
          }

          @for (entity of individual.flaggedEntities; track $index) {
            <div class="entity">
              <div class="entity-row"
                [class.entity-row-expandable]="entity.adverseGroups.length"
                (click)="toggleEntity(entity)">
                @if (entity.adverseGroups.length) {
                  <span class="entity-chevron mdi"
                    [class.mdi-chevron-right]="!isShowingItems(entity)"
                    [class.mdi-chevron-down]="isShowingItems(entity)"></span>
                } @else {
                  <span class="entity-chevron"></span>
                }
                <span class="entity-name">{{entity.organisationName}}</span>
                @if (entity.highRisk) {
                  <span class="entity-flag" matTooltip="Flagged high risk by CreditorWatch">High risk</span>
                }
                <span class="entity-meta">
                  @for (tally of entity.tallies; track tally.shortLabel) {
                    <span class="entity-tally">{{tally.count}} {{tally.shortLabel}}</span>
                  }
                  @if (entity.organisationStatus) {
                    <span class="entity-status" [class.entity-status-adverse]="entity.statusIsAdverse">
                      {{entity.organisationStatus}}
                    </span>
                  }
                </span>
              </div>

              @if (isShowingItems(entity)) {
                <div class="entity-items">
                  @for (group of entity.adverseGroups; track group.title) {
                    <div class="adverse-group">
                      <div class="adverse-group-title">{{group.title}} ({{group.items.length}})</div>
                      @for (item of group.items; track $index) {
                        <div class="adverse-item">
                          <div class="adverse-item-headline" (click)="toggleFields(item)">
                            <span class="mdi" [class.mdi-chevron-right]="!isShowingFields(item)" [class.mdi-chevron-down]="isShowingFields(item)"></span>
                            <span>{{item.headline}}</span>
                          </div>
                          @if (isShowingFields(item)) {
                            <div class="adverse-fields">
                              @for (field of item.fields; track field.label) {
                                <div class="adverse-field">
                                  <span class="adverse-field-label">{{field.label}}</span>
                                  <span class="adverse-field-value">{{field.value}}</span>
                                </div>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }

          @if (individual.clearEntities.length) {
            <div class="clear-entities">
              <span class="clear-entities-toggle" (click)="toggleClearEntities(individual)">
                <span class="mdi" [class.mdi-chevron-right]="!isShowingClearEntities(individual)" [class.mdi-chevron-down]="isShowingClearEntities(individual)"></span>
                {{individual.clearEntities.length}} other
                {{individual.clearEntities.length === 1 ? 'entity' : 'entities'}} with no adverse items
              </span>
              @if (isShowingClearEntities(individual)) {
                <div class="clear-entities-list">
                  @for (entity of individual.clearEntities; track $index) {
                    <span class="clear-entity">{{entity.organisationName}}</span>
                  }
                </div>
              }
            </div>
          }

          @if (!individual.flaggedEntities.length && !individual.clearEntities.length) {
            <div class="no-data">No related entities</div>
          }
        </div>
      }
    } @else {
      <div class="no-data">No Data</div>
    }

    <div class="report-actions" fxLayout="row" fxLayoutAlign="start center">
      <button mat-stroked-button (click)="onCancel()">Close</button>
    </div>
  </mat-dialog-content>
</div>
